import React, { useEffect, useState } from "react";
import { IoIosArrowRoundBack, IoMdTv } from "react-icons/io";
import PRofileID from "../../../../helper/svg/ID.svg";
import Smartphone from "../../../../helper/svg/smartphone.svg";
import Avtar from "../../../../helper/images/Avtar.png";
import { TbMailFilled } from "react-icons/tb";
import { FaLocationDot } from "react-icons/fa6";
import { Link, useNavigate, useParams } from "react-router-dom";
import Barthroom from "../../../../helper/svg/bathroom.svg";
import Kitchen from "../../../../helper/svg/kitchen.svg";
import languages from "../../../../helper/svg/Language.svg";
import FoodOrder from "../../../../helper/svg/Foodorder.svg";
import Wifi from "../../../../helper/svg/wifi.svg";
import Resto1 from "../../../../helper/images/hoteldetail1.png";
import HotelImagesSlider from "./HotelImageSlider/HotelImageSlider";
import { IoStar } from "react-icons/io5";
import { RiHotelLine } from "react-icons/ri";
import { GoHome } from "react-icons/go";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getHotelById,
  updateHotelPlatformFee,
  updateHotelStatus,
} from "../../../../api/admin/hotelManagement";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import {
  MdWifi,
  MdPool,
  MdFitnessCenter,
  MdSpa,
  MdLocalParking,
  MdRestaurant,
  MdLocalBar,
  MdAcUnit,
  MdPets,
} from "react-icons/md";
import { useTranslation } from "../../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const HotelDetailPage = () => {
  const { t } = useTranslation();
  const translations = t("adminHotelScreen");
  const { hotelId } = useParams();
  const [enabled, setEnabled] = useState(true);
  const [rejectionReason, setRejectionReason] = useState("");
  const queryClient = useQueryClient();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [platformFee, setPlatformFee] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["hotel", hotelId],
    queryFn: () => getHotelById(hotelId),
    enabled: !!hotelId,
  });

  const statusMutation = useMutation({
    mutationFn: (payload) => updateHotelStatus(hotelId, payload),
    onSuccess: () => {
      toast.success(t("adminHotelScreen").statusUpdateSuccess);
      queryClient.invalidateQueries(["hotel", hotelId]);
    },
    onError: () => toast.error("Failed to update status"),
  });

  const platformFeeMutation = useMutation({
    mutationFn: (payload) => updateHotelPlatformFee(hotelId, payload),
    onSuccess: () => {
      toast.success(t("adminHotelScreen").platformFeeSuccess);
      queryClient.invalidateQueries(["hotel", hotelId]);
      setIsPopupOpen(false);
    },
    onError: () => toast.error(t("adminHotelScreen").platformFeeError),
  });

  const hotel = data?.data;

  const handleApprove = () => {
    statusMutation.mutate({
      verificationStatus: "approved",
      rejectionReason: "",
    });
  };

  const handleReject = () => {
    if (!rejectionReason)
      return toast.error(t("adminHotelScreen").rejectionReasonError);
    statusMutation.mutate({
      verificationStatus: "rejected",
      rejectionReason,
    });
  };

  const handleChange = (e) => {
    if (e.target.value > 100) {
      setError(t("adminHotelScreen").invalidPercentageError);
    } else {
      setError("");
    }
    setPlatformFee(e.target.value);
  };

  const onUpdatePlatformFee = () => {
    // Check if input is empty
    if (!platformFee || platformFee.toString().trim() === "") {
      setError(t("adminHotelScreen").platformFeeEmptyError || "Platform fee cannot be empty");
      return;
    }
    
    // Check if value is greater than 100
    if (platformFee > 100) {
      setError(t("adminHotelScreen").invalidPercentageError);
      return;
    }
    
    // Clear any previous errors
    setError("");
    platformFeeMutation.mutate({ platformFee });
  };

  const openModal = () => {
    setPlatformFee(hotel?.platformFee || "");
    setError("");
    setIsPopupOpen(true);
  };

  const closeModal = () => {
    setIsPopupOpen(false);
    setError("");
  };

  const facilityIcons = {
    "Free Wi-Fi": MdWifi,
    "Swimming Pool": MdPool,
    "Fitness Center": MdFitnessCenter,
    Spa: MdSpa,
    Parking: MdLocalParking,
    Restaurant: MdRestaurant,
    Bar: MdLocalBar,
    "Air Conditioning": MdAcUnit,
    "Pets Allowed": MdPets,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div
          onClick={() => navigate(-1)}
          className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]"
        >
          <IoIosArrowRoundBack size={24} className="text-[#fff]" />
        </div>
        <h2 className="text-[22px] font-medium">
          {translations?.HotelManagement}
        </h2>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-[30px] mt-[30px]">
        <div className="2xl:p-[38px] lg:p-[25px] p-[15px] border border-[#DDDDDD] rounded-[10px] bg-[#fff] -full">
          <HotelImagesSlider images={hotel?.images || []} />
          <div className="mt-[45px]">
            <h2 className="md:text-[25px] text-[20px] font-semibold text-black mb-[11px] leading-[50px]">
              {hotel?.name}
            </h2>
            <p className="text-lg text-black mb-5 leading-[22px]">
              {hotel?.totalBedrooms} {translations?.bedrooms},{" "}
              {hotel?.washrooms} {translations?.bathroom}
            </p>
            <div className="flex flex-wrap gap-[14px] w-full mb-[20px]">
              <p className="text-lg text-black font-medium leading-[22px]">
                {translations?.Reviews} {hotel?.rating} / 5
              </p>
              <div className="flex items-center gap-[2px]">
                {Array(Math.floor(hotel?.rating))
                  .fill()
                  .map((_, index) => (
                    <MdStar
                      key={`full-${index}`}
                      size={21}
                      className="text-[#FF9D00]"
                    />
                  ))}

                {hotel?.rating % 1 !== 0 && (
                  <MdStarHalf size={21} className="text-[#FF9D00]" />
                )}

                {Array(5 - Math.ceil(hotel?.rating))
                  .fill()
                  .map((_, index) => (
                    <MdStarOutline
                      key={`empty-${index}`}
                      size={21}
                      className="text-[#FF9D00]"
                    />
                  ))}
              </div>
            </div>

            {hotel?.verificationStatus === "approved" && (
              <div className="flex items-center justify-between mb-[16px]">
                <h3 className="text-[18px] font-medium leading-[22px]">
                  <span>{translations?.PlatformFee}</span>
                </h3>
                <div className="flex items-center">
                  <p className="text-[16px] text-gray-800 mr-3">
                    {hotel?.platformFee}%
                  </p>
                  <button
                    onClick={openModal}
                    className="text-blue-500 hover:text-blue-700 focus:outline-none"
                  >
                    <FaEdit className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* <div className="pt-[26px] border-t border-[#D9D9D9] pb-5">
               <div className="grid grid-cols-[24px_1fr] gap-[20px] mb-[17px]">
                <RiHotelLine className="text-black text-[24px]" />
                <div>
                  <h3 className="text-black  text-[18px] leading-[22x] font-medium mb-[11px]">
                    Room in a bed and breakfast
                  </h3>
                  <p className="text-[15px] text-black leading-[18px]">
                    Your own room in a home, plus access to shared spaces.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-[24px_1fr] gap-[20px] mb-[17px]">
                <GoHome className="text-black text-[24px]" />
                <div>
                  <h3 className="text-black  text-[18px] leading-[22x] font-medium mb-[10px]">
                    Shared common spaces
                  </h3>
                  <p className="text-[15px] text-black leading-[18px]">
                    You’ll share parts of the home.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-[24px_1fr] gap-[20px] mb-[17px]">
                <img className="text-black text-[24px]" src={Barthroom} />
                <div>
                  <h3 className="text-black  text-[18px] leading-[22x] font-medium mb-[10px]">
                    Shared bathroom
                  </h3>
                  <p className="text-[15px] text-black leading-[18px]">
                    You’ll share the bathroom with others.
                  </p>
                </div>
              </div> 
            </div> */}

            <div className="pt-[26px] border-t border-[#D9D9D9] pb-5">
              <div
                dangerouslySetInnerHTML={{ __html: hotel?.description }}
                className="text-[#333] text-[16px] leading-[26px]"
              />
            </div>

            <div className="pt-5 border-t border-[#D9D9D9] pb-5">
              <h2 className="text-xl text-black font-semibold mb-5">
                {translations?.AboutThisPlace}
              </h2>
              <p className="te-lg leading-[30px]">{hotel?.extras}</p>
            </div>

            <div className="grid md:grid-cols-2 md:gap-[24px] gap-3">
              {hotel?.facilities?.map((facility, index) => {
                const Icon = facilityIcons[facility];

                return (
                  <div
                    className="grid grid-cols-[24px_1fr] gap-[14px] mb-[15px]"
                    key={index}
                  >
                    {Icon ? (
                      <Icon className="text-[20px] text-black" stroke="1.5" />
                    ) : (
                      <div className="text-black text-[20px]">•</div>
                    )}
                    <h3 className="text-black text-[18px] leading-[22px]">
                      {facility}
                    </h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div>
          <div className="w-full xl:max-w-[360px] border border-[#DDDDDD] rounded-[12px] bg-white p-2">
            <div className="bg-[#FFFAF2] h-[132px] rounded-[3.56px]"></div>
            <img
              src={
                hotel.ownerId?.profilePicture
                  ? `${hotel.ownerId.profilePicture}`
                  : Avtar
              }
              alt="avatar"
              className="w-full max-w-[159px] h-[159px] mx-auto rounded-full border-4 border-white -mt-[80px]"
            />
            <h2 className="text-xl font-medium text-center mt-5">
              {hotel?.ownerId?.name || "N/A"}
            </h2>
            <div className="bg-[#E1EDDE] px-2 py-1 text-center text-[#1B7200] rounded w-max mx-auto mt-[7px] mb-5">
              {translations?.Owner}
            </div>

            <div className="py-5 px-7 border-t border-[#E0E2E7]">
              <ul className="space-y-4">
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <img src={PRofileID} alt="" />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {translations?.UserID}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {hotel?.ownerId?._id}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <TbMailFilled />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {" "}
                      {translations?.Email}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {hotel?.ownerId?.email}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <FaLocationDot />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {" "}
                      {translations?.Address}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {hotel?.ownerId?.address || "N/A"}
                    </p>
                  </div>
                </li>
                <li className="flex gap-[14px]">
                  <span className="bg-[#F0F1F3] rounded-full w-[36px] h-[36px] flex items-center justify-center">
                    <img src={Smartphone} alt="" />
                  </span>
                  <div>
                    <h5 className="text-[#667085] text-base">
                      {translations?.Phone}
                    </h5>
                    <p className="text-[#353535] text-base">
                      {hotel?.ownerId?.phoneNumber}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {hotel?.verificationStatus !== "approved" && (
            <>
              {hotel?.verificationStatus === "rejected" ? (
                <div className="mt-[37px] w-full rounded border border-[#DDDDDD] p-[16px] bg-[#FFFAF2]">
                  <p className="text-[#18181B]">
                    {hotel?.rejectionReason || "No rejection reason provided."}
                  </p>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleApprove}
                    className="mt-[37px] w-full rounded bg-[#4CAF50] text-white text-center font-bold py-3 hover:bg-[#209b25]"
                  >
                    {translations?.ApproveHotel}
                  </button>

                  <textarea
                    placeholder="Provide a reason for rejection"
                    className="w-full h-[154px] mt-[20px] rounded border border-[#DDDDDD] p-[16px] text-[#18181B] placeholder:text-[#BEBEBE]"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>

                  <button
                    onClick={handleReject}
                    className="mt-[18px] w-full rounded bg-[#FF0000] text-white text-center font-bold py-3 hover:bg-[#D62323]"
                  >
                    {translations?.RejectHotel}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={closeModal}>
        <div className="p-[30px]">
          <div className="pb-[21px] border-b border-[#EEEEEE] mb-[19px]">
            <h2 className="text-[22px] font-medium">
              {" "}
              {translations?.UpdatePlatformFee}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-[16px] mb-[19px]">
            <div className="w-full">
              <label className="text-sm mb-[8px] block">
                {translations?.PlatformFee} (%)
              </label>
              <div className="relative w-full">
                <input
                  type="number"
                  value={platformFee}
                  onChange={handleChange}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${error ? "border-red-500" : "focus:border-[#4CAF50]"
                    }`}
                  placeholder={translations?.platformFeePlaceholder}
                  min="0"
                  max="100"
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>
            </div>

            <div className="flex gap-[30px] justify-end pt-[23px]">
              <button
                onClick={closeModal}
                className="text-white py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] font-bold"
              >
                {translations?.CancelButton}
              </button>
              <button
                onClick={onUpdatePlatformFee}
                className="bg-[#4CAF50] text-white py-[14px] px-[30px] rounded-[2px] text-[20px] font-bold hover:bg-[#209b25]"
              >
                {translations?.UpdateButton}
              </button>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default HotelDetailPage;
